import { SyntheticEvent, useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Link, Typography } from '@mui/material';

import CardForm from './CardForm';
import MessageCenterLink from './MessageCenterLink';

import Dialog from 'modules/common/components/MUI/Dialog';
import { useMemberPaymentContext } from 'modules/common/components/Payment/MemberPaymentContext';
import config from 'modules/common/config';
import useAuthHandlers from 'modules/Auth/AuthProvider/useAuthHandlers';
import useIMC from 'modules/IMC/useIMC';

export default function UpdatePaymentModal() {
    const {
        isPaymentModalOpen,
        hasPaymentIssue,
        stripeTokenId,
        isToSChecked,
        stripeMessage,
        isUpdating,
        closePaymentModal,
        setIsToSChecked,
        updatePayment,
    } = useMemberPaymentContext();

    const { logout } = useAuthHandlers();
    const { currOpenDrawer } = useIMC();
    const [isCreatingToken, setIsCreatingToken] = useState(false);

    // Lazy-mount the Dialog (and its Stripe iframes) only after the user has opened the
    // modal for the first time. Once mounted, keep it mounted via Dialog's `keepMounted`
    // prop so an IMC drawer overlay doesn't unmount the CardForm and lose the user's
    // in-progress card data. This avoids paying the Stripe-iframe cost on every member
    // page that simply renders <Layout>.
    const [hasBeenOpened, setHasBeenOpened] = useState(false);
    useEffect(() => {
        if (!hasBeenOpened && (isPaymentModalOpen || hasPaymentIssue)) {
            setHasBeenOpened(true);
        }
    }, [hasBeenOpened, isPaymentModalOpen, hasPaymentIssue]);

    const handleClose = () => {
        if (hasPaymentIssue) {
            return;
        }
        closePaymentModal();
    };

    const handleTosChange = (_e: SyntheticEvent, checked: boolean) => {
        setIsToSChecked(checked);
    };

    const handleUpdate = () => {
        updatePayment();
    };

    const isUpdateDisabled = isUpdating || stripeTokenId === '' || !isToSChecked;

    if (!hasBeenOpened) {
        return null;
    }

    return (
        <Dialog
            data-testid="update-payment-modal"
            keepMounted
            open={!currOpenDrawer && (isPaymentModalOpen || hasPaymentIssue)}
            onClose={handleClose}
            heading="Payment Update"
            actions={
                <Box sx={{ display: 'flex', gap: 2 }} data-testid="update-payment-modal-actions">
                    {!hasPaymentIssue ? (
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={handleClose}
                            data-testid="update-payment-modal-cancel">
                            Cancel
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            color="primary"
                            size="large"
                            onClick={() => logout()}
                            data-testid="update-payment-modal-logout">
                            Sign out
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        size="large"
                        disabled={isUpdateDisabled}
                        loading={isUpdating || isCreatingToken}
                        onClick={handleUpdate}
                        data-testid="update-payment-modal-update">
                        Update
                    </Button>
                </Box>
            }>
            {hasPaymentIssue && !isUpdating && !stripeMessage.isSuccess && (
                <Typography variant="body1" color="error" sx={{ pb: 2 }}>
                    There was an error processing the payment. If you need help or the problem persists, please send us
                    a message using the <MessageCenterLink />.
                </Typography>
            )}
            {isUpdating && (
                <Typography variant="body1" color="text.secondary" sx={{ pb: 2 }}>
                    Updating your payment method... If you need help or the problem persists, please send us a message
                    using the <MessageCenterLink color="primary" />.
                </Typography>
            )}
            {!stripeMessage.firstTime && stripeMessage.message && !isUpdating && (
                <Alert severity={stripeMessage.isSuccess ? 'success' : 'error'} sx={{ mb: 2 }}>
                    <Typography variant="body2">{stripeMessage.message}</Typography>
                    {stripeMessage.messageLine1 && (
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {stripeMessage.messageLine1} For more information,{' '}
                            <Link href={stripeMessage.messageLine2} target="_blank" rel="noopener noreferrer">
                                click here
                            </Link>
                        </Typography>
                    )}
                </Alert>
            )}
            <CardForm setIsCreatingToken={setIsCreatingToken} />
            <FormControlLabel
                sx={{ mt: 4 }}
                onChange={handleTosChange}
                checked={isToSChecked}
                control={<Checkbox />}
                label={
                    <Typography variant="body2">
                        I read & agree {config.companyName}{' '}
                        <Link
                            href={`${config.companyURL}/terms-of-service.html`}
                            target="_blank"
                            rel="noopener noreferrer">
                            Terms of Service{' '}
                        </Link>
                        and
                        <Link
                            href={`${config.companyURL}/privacy-policy.html`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ ml: 1 }}>
                            Privacy Policy
                        </Link>
                    </Typography>
                }
            />
        </Dialog>
    );
}
